import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Vote, ShieldCheck, MapPin, ClipboardList, ArrowUpRight, FileText, FileSpreadsheet, AlertTriangle, Play, StopCircle, Loader2, Trophy, Check, X } from "lucide-react";

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ elections: 0, candidates: 0, locations: 0, pending: 0, approved: 0, users: 0, flagged: 0, reported: 0, totalPU: 0, reportPct: 0 });
  const [elections, setElections] = useState([]);
  const [rehearsal, setRehearsal] = useState({ enabled: false, injected: 0 });
  const [rBusy, setRBusy] = useState(false);
  const [regions, setRegions] = useState([]);
  const [presetRegionId, setPresetRegionId] = useState("");
  const [leaderboard, setLeaderboard] = useState([]);
  const [ocrMin, setOcrMin] = useState(0);
  const [ocrSaving, setOcrSaving] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== "undefined" ? localStorage.getItem("ec_token") : "";
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    (async () => {
      const [el, locs, res] = await Promise.all([
        api.get("/elections"),
        api.get("/locations"),
        api.get("/results"),
      ]);
      setElections(el.data);
      // Reporting % for the active election (or first) — always visible on the console
      let reported = 0, totalPU = 0, reportPct = 0;
      const active = el.data.find(e => e.status === "active") || el.data[0];
      if (active) {
        try {
          const { data: col } = await api.get(`/elections/${active.id}/collation`, { params: { level: "national", include_pending: true }});
          reported = col?.meta?.polling_units_reported || 0;
          totalPU = col?.total_polling_units || 0;
          reportPct = totalPU ? Math.min(100, Math.round((reported / totalPU) * 100)) : 0;
        } catch {}
      }
      setStats({
        elections: el.data.length,
        locations: locs.data.length,
        pending: res.data.filter(r => r.status === "pending").length,
        approved: res.data.filter(r => r.status === "approved").length,
        flagged: res.data.filter(r => (r.flags || []).length > 0 && !r.superseded_by).length,
        candidates: 0,
        users: 0,
        reported, totalPU, reportPct,
      });
      try {
        const { data: lb } = await api.get("/reviewers/leaderboard");
        setLeaderboard(lb || []);
      } catch {}
      try {
        const { data: oc } = await api.get("/system/ocr-config");
        setOcrMin(oc?.min_confidence || 0);
      } catch {}
    })();
  }, []);

  const saveOcrThreshold = async (v) => {
    setOcrSaving(true);
    try {
      const { data } = await api.post("/system/ocr-config", { min_confidence: v });
      setOcrMin(data.min_confidence);
      toast.success(`OCR threshold set to ${Math.round(data.min_confidence*100)}%`);
    } catch (e) { toast.error("Save failed"); }
    finally { setOcrSaving(false); }
  };

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/rehearsal/status").then(({ data }) => setRehearsal(data)).catch(() => {});
    api.get("/locations", { params: { level: "state", parent_id: "root" }}).then(({ data }) => setRegions(data)).catch(() => {});
  }, [isAdmin]);

  // Rehearsal ticker: while enabled, fire /rehearsal/tick every 30s
  useEffect(() => {
    if (!isAdmin || !rehearsal.enabled) return;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      try {
        const { data } = await api.post("/rehearsal/tick");
        if (data.submitted) {
          setRehearsal(r => ({ ...r, injected: (r.injected || 0) + 1 }));
        } else if (data.reason === "all_units_have_results") {
          toast.warning("Every polling unit already has a result — nothing left to simulate.");
          stopped = true;
        }
      } catch (e) {
        toast.error(formatDetail(e.response?.data?.detail) || "Rehearsal tick failed");
      }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { stopped = true; clearInterval(id); };
  }, [isAdmin, rehearsal.enabled]);

  const startRehearsal = async () => {
    setRBusy(true);
    try {
      const { data } = await api.post("/rehearsal/start");
      setRehearsal({ enabled: true, started_at: data.started_at, injected: 0 });
      toast.success("Rehearsal started — simulated transmissions will fire every 30 seconds");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Could not start rehearsal"); }
    finally { setRBusy(false); }
  };
  const setFieldPreset = async () => {
    try {
      const payload = presetRegionId ? { region_id: presetRegionId, pick_count: 4 } : { pick_count: 4 };
      const { data } = await api.post("/rehearsal/focus", payload);
      toast.success(`Field rehearsal: focused on ${data.names.length} constituencies — ${data.names.join(", ")}`);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Could not set preset"); }
  };
  const clearFieldPreset = async () => {
    try { await api.post("/rehearsal/focus", { ward_ids: [] }); toast.info("Focus cleared — rehearsal picks from all polling units"); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Could not clear"); }
  };
  const stopRehearsal = async () => {
    setRBusy(true);
    try {
      const { data } = await api.post("/rehearsal/stop");
      setRehearsal({ enabled: false, injected: 0 });
      toast.success(`Rehearsal stopped · ${data.purged} synthetic result${data.purged===1?"":"s"} purged`);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Could not stop rehearsal"); }
    finally { setRBusy(false); }
  };

  const downloadSigned = async (eid, purpose) => {
    try {
      const { data } = await api.post("/downloads/sign", { purpose, election_id: eid, ttl_seconds: 300 });
      const path = purpose === "election_pdf" ? "export/pdf" : "export";
      window.open(`${backend}/api/elections/${eid}/${path}?dt=${encodeURIComponent(data.token)}`, "_blank", "noreferrer");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Could not sign download link"); }
  };

  const cards = [
    { label: "Elections", value: stats.elections, icon: Vote, to: "/app/elections" },
    { label: "Locations", value: stats.locations, icon: MapPin, to: "/app/hierarchy" },
    { label: "Pending Approval", value: stats.pending, icon: ClipboardList, to: "/app/approvals", accent: "#FF9F0A" },
    { label: "Approved Results", value: stats.approved, icon: ShieldCheck, to: "/app/approvals", accent: "#34C759" },
  ];

  const showAdminActions = user?.role === "admin" || user?.role === "observer";

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]">
      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Console Overview</div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2" data-testid="overview-title">Welcome, {user?.name}</h1>
      <p className="text-zinc-400 mt-2 text-sm">Signed in as <span className="text-white uppercase tracking-wider">{user?.role === "observer" ? "Reviewer" : user?.role}</span></p>

      {isAdmin && (
        <div className={`mt-8 border p-5 flex items-center gap-4 flex-wrap ${rehearsal.enabled ? "border-amber-400/50 bg-amber-400/[0.06]" : "border-white/10 bg-[#111114]"}`} data-testid="rehearsal-card">
          <div className={`w-10 h-10 flex items-center justify-center ${rehearsal.enabled ? "bg-amber-400 text-black" : "bg-zinc-800 text-zinc-500"}`}>
            {rehearsal.enabled ? <Loader2 size={18} className="animate-spin"/> : <Play size={16}/>}
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Election-Day Rehearsal</div>
            <div className="text-sm mt-1">
              {rehearsal.enabled
                ? <>Rehearsal is <span className="text-amber-300 font-semibold">RUNNING</span> — simulated transmissions every 30s · <span className="tabular">{rehearsal.injected}</span> injected</>
                : <>Rehearse election-day without touching real data. One click fires simulated approved transmissions every 30s.</>}
            </div>
          </div>
          {rehearsal.enabled ? (
            <>
              <select value={presetRegionId} onChange={e=>setPresetRegionId(e.target.value)} data-testid="rehearsal-region-select"
                className="bg-transparent border border-white/20 px-2 py-1.5 text-[11px] outline-none">
                <option value="" className="bg-black">All Ghana (random)</option>
                {regions.map(r => <option key={r.id} value={r.id} className="bg-black">{r.name}</option>)}
              </select>
              <button onClick={setFieldPreset} data-testid="rehearsal-field-preset"
                className="px-3 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10">
                Field preset (4)
              </button>
              <button onClick={clearFieldPreset} data-testid="rehearsal-clear-preset"
                className="px-3 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10">
                Clear focus
              </button>
              <button onClick={stopRehearsal} disabled={rBusy} data-testid="rehearsal-stop-btn"
                className="px-4 py-2 bg-red-500 text-white text-xs uppercase tracking-[0.2em] font-semibold hover:bg-red-400 disabled:opacity-40 flex items-center gap-2">
                {rBusy ? <Loader2 size={13} className="animate-spin"/> : <StopCircle size={13}/>} Stop + purge
              </button>
            </>
          ) : (
            <button onClick={startRehearsal} disabled={rBusy} data-testid="rehearsal-start-btn"
              className="px-4 py-2 bg-amber-400 text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-amber-300 disabled:opacity-40 flex items-center gap-2">
              {rBusy ? <Loader2 size={13} className="animate-spin"/> : <Play size={13}/>} Start rehearsal
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        {cards.map(c => (
          <Link to={c.to} key={c.label} className="group border border-white/10 bg-[#111114] p-5 hover:border-white/30 transition-colors" data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="flex items-center justify-between">
              <c.icon size={16} strokeWidth={1.5} style={{ color: c.accent || "#a1a1aa" }} />
              <ArrowUpRight size={14} className="opacity-30 group-hover:opacity-100 transition-opacity text-zinc-500" />
            </div>
            <div className="font-display text-4xl mt-4 tabular" style={{ color: c.accent || "#fff" }}>{c.value.toLocaleString()}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 mt-1">{c.label}</div>
          </Link>
        ))}
      </div>

      <div className="mt-6 border border-emerald-500/25 bg-emerald-500/[0.04] p-5" data-testid="overview-reporting-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/80 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"/>Polling Station Reporting
            </div>
            <div className="mt-2 flex items-baseline gap-3">
              <div className="font-display text-5xl tabular text-white" data-testid="overview-reporting-pct">{stats.reportPct}%</div>
              <div className="text-sm text-zinc-400 tabular">
                <span className="text-white font-medium">{stats.reported.toLocaleString()}</span> of <span className="text-white font-medium">{stats.totalPU.toLocaleString()}</span> polling stations reported
              </div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Includes pending + approved</div>
        </div>
        <div className="mt-4 h-1.5 bg-white/5 relative overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-emerald-500 transition-[width] duration-700" style={{ width: `${stats.reportPct}%` }} />
        </div>
      </div>

      {isAdmin && (
        <div className="mt-6 border border-emerald-500/20 bg-emerald-500/[0.03] p-4" data-testid="ocr-threshold-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-semibold">OCR Confidence Threshold</div>
              <div className="text-xs text-zinc-400 mt-1">Votes read below this confidence are left blank instead of pre-filled — agents fill them in manually.</div>
            </div>
            <div className="flex items-center gap-3">
              <input type="range" min="0" max="100" step="5" value={Math.round(ocrMin*100)}
                onChange={e=>setOcrMin(parseInt(e.target.value)/100)}
                onMouseUp={e=>saveOcrThreshold(parseInt(e.target.value)/100)}
                onTouchEnd={e=>saveOcrThreshold(parseInt(e.target.value)/100)}
                data-testid="ocr-threshold-slider"
                className="w-48 accent-emerald-500"/>
              <div className="font-display text-2xl tabular text-emerald-300 w-12 text-right">{Math.round(ocrMin*100)}%</div>
              {ocrSaving && <Loader2 size={14} className="animate-spin text-emerald-400"/>}
            </div>
          </div>
        </div>
      )}

      {leaderboard.length > 0 && (
        <div className="mt-6 border border-white/10 bg-[#111114]" data-testid="reviewer-leaderboard">
          <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
            <Trophy size={13} className="text-amber-400"/>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">Top Reviewers · last 24h</div>
          </div>
          <div className="divide-y divide-white/5">
            {leaderboard.map((r, i) => (
              <div key={r.email} className="px-5 py-3 flex items-center gap-4" data-testid={`leaderboard-row-${i}`}>
                <div className={`w-6 text-center font-display text-xl tabular ${i===0?"text-amber-400":i===1?"text-zinc-300":i===2?"text-orange-400":"text-zinc-500"}`}>{i+1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{r.name || r.email}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{r.role_label} · last {(r.last_at||"").replace("T"," ").slice(11,16)}</div>
                </div>
                <div className="flex items-center gap-3 text-xs tabular">
                  <span className="flex items-center gap-1 text-emerald-400" title="Approvals"><Check size={12}/> {r.approvals}</span>
                  <span className="flex items-center gap-1 text-red-400" title="Rejections"><X size={12}/> {r.rejections}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showAdminActions && stats.flagged > 0 && (
        <Link to="/app/fraud" data-testid="fraud-alert-card"
          className="mt-6 flex items-center gap-4 border border-red-500/40 bg-red-500/[0.06] hover:bg-red-500/[0.12] transition-colors p-4">
          <AlertTriangle className="text-red-400 shrink-0" size={18}/>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.2em] text-red-300">{stats.flagged} submission{stats.flagged>1?"s":""} flagged</div>
            <div className="text-sm text-zinc-300 mt-0.5">Open the Fraud Board to triage the riskiest polling units first.</div>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-red-200">Fraud Board →</div>
        </Link>
      )}

      <div className="mt-12 border border-white/10">
        <div className="px-5 py-3 border-b border-white/10 text-xs uppercase tracking-[0.3em] text-zinc-500 flex items-center justify-between">
          <span>Active Elections</span>
          <Link to="/" className="text-emerald-400 hover:underline underline-offset-4">Public Dashboard →</Link>
        </div>
        <div className="divide-y divide-white/5">
          {elections.map(e => (
            <div key={e.id} className="px-5 py-4 flex items-center gap-4 hover:bg-white/[0.02] transition-colors">
              <div className={`w-2 h-8 ${e.status === "active" ? "bg-emerald-500" : e.status === "closed" ? "bg-zinc-600" : "bg-amber-400"}`} />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{e.name}</div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">{e.election_type} · {e.election_date}</div>
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{e.status}</div>
              {showAdminActions && (
                <div className="flex items-center gap-1">
                  <button onClick={() => downloadSigned(e.id, "election_pdf")} data-testid={`pdf-${e.id}`}
                    className="px-2.5 py-1.5 border border-white/15 hover:bg-white/10 text-[10px] uppercase tracking-[0.15em] flex items-center gap-1.5">
                    <FileText size={12}/> PDF
                  </button>
                  <button onClick={() => downloadSigned(e.id, "election_csv")} data-testid={`csv-${e.id}`}
                    className="px-2.5 py-1.5 border border-white/15 hover:bg-white/10 text-[10px] uppercase tracking-[0.15em] flex items-center gap-1.5">
                    <FileSpreadsheet size={12}/> CSV
                  </button>
                </div>
              )}
            </div>
          ))}
          {!elections.length && <div className="px-5 py-8 text-zinc-500 text-sm">No elections yet. <Link to="/app/elections" className="underline">Create one →</Link></div>}
        </div>
      </div>
    </div>
  );
}
